import { FunctionComponent, ChangeEvent } from 'react';
import styles from './TextInput.module.css';

interface TextInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hasNextButton?: boolean;
  onNext?: () => void;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  required?: boolean;
  helperText?: string;
  error?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'url' | 'password';
}

const TextInput: FunctionComponent<TextInputProps> = ({
  id,
  label,
  value,
  onChange,
  hasNextButton = false,
  onNext,
  multiline = false,
  rows = 4,
  maxLength,
  required = false,
  helperText,
  error,
  placeholder,
  type = 'text'
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const handleNext = () => {
    if (required && !value.trim()) {
      return;
    }
    if (onNext) {
      onNext();
    }
  };

  const isNextDisabled = required && !value.trim();
  
  const getCharacterCounterClass = () => {
    if (!maxLength) return styles.characterCounter;
    
    const remaining = maxLength - value.length;
    if (remaining < 0) {
      return `${styles.characterCounter} ${styles.error}`;
    } else if (remaining <= maxLength * 0.1) {
      return `${styles.characterCounter} ${styles.warning}`;
    }
    return styles.characterCounter;
  };

  const inputProps = {
    id,
    value,
    onChange: handleChange,
    required,
    placeholder,
    className: `${styles.input} ${error ? styles.error : ''}`,
    maxLength,
    'aria-describedby': [
      helperText && `${id}-helper`,
      error && `${id}-error`,
      maxLength && `${id}-counter`
    ].filter(Boolean).join(' ') || undefined
  };

  return (
    <div className={styles.container}>
      <label htmlFor={id} className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      
      <div className={styles.inputWrapper}>
        {multiline ? (
          <textarea
            {...inputProps}
            rows={rows}
            className={`${inputProps.className} ${styles.textarea}`}
          />
        ) : (
          <input
            {...inputProps}
            type={type}
          />
        )}
      </div>

      {maxLength && (
        <div id={`${id}-counter`} className={getCharacterCounterClass()}>
          {value.length}/{maxLength}
        </div>
      )}

      {helperText && (
        <p id={`${id}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}
      
      {error && (
        <p id={`${id}-error`} className={styles.errorText}>
          {error}
        </p>
      )}

      {hasNextButton && onNext && (
        <div className={styles.nextButtonContainer}>
          <button
            type="button"
            className={styles.nextButton}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TextInput;